import React, { useContext, useEffect, useState } from 'react';
import StatusChartWidget from '../../../components/Widgets/StatusChartWidget';
import { LarianTechAssetWidget } from '../../../components/Widgets/LarianTechWidget';
import TimelineWidget from '../../../components/Widgets/TimelineWidget';
import OverviewWidget from '../../../components/Widgets/OverviewWidget';
import ContractWidget from '../../../components/Widgets/ContractWidget';
import WidgetDashboard from '../../../components/WidgetDashboard';
import SettingsContext from '../../../context/SettingsContext';
import useAsync from '../../../hooks/useAsync';
import { getWidgetsByType } from '../../../api/company';
import {
	getAvailableWidgets,
	TAG_COLOR_OPTIONS,
} from '../../../components/helpers/constants';
import { isEqual } from 'lodash';
import { getModuleCategories, getModuleFieldsBeta } from '../../../api/modules';
import { getLocations, updateAsset } from '../../../api';
import {
	classNames,
	createDataTree,
	getUserFullName,
	objectAccess,
} from '../../../components/helpers/functions';
import ReactSelect, { components } from 'react-select';
import {
	ChevronDownIcon,
	ChevronLeftIcon,
	PlusCircleIcon,
	PlusIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import usePrevious from '../../../hooks/usePrevious';
import Button from '../../../components/Button';
import useModal from '../../../hooks/useModal';
import {
	TemplateField,
	TemplateReactSelect,
} from '../../../components/Attributes/Attributes_old';
import { getContacts } from '../../../api/contacts';
import useSettings from '../../../hooks/useSettings';
import { getTags } from '../../../api/tags';
import Badge from '../../../components/Badge';
import { Menu } from '@headlessui/react';

const ASSETS_MODULE_ID = process.env.REACT_APP_MODULE_ID_ASSETS;

const DropdownIndicator = (props) => {
	return (
		<components.DropdownIndicator {...props}>
			<ChevronDownIcon className="w-4 h-4 text-gray-500" />
		</components.DropdownIndicator>
	);
};

const ClearIndicator = (props) => {
	return (
		<components.ClearIndicator {...props}>
			<XMarkIcon className="w-8 h-8 text-gray-500 px-2 py-1 cursor-pointer" />
		</components.ClearIndicator>
	);
};

const CustomMenuList = ({ customAction, ...props }) => {
	return (
		<components.MenuList {...props}>
			{props.children}
			{customAction ? (
				<>
					<div className="border-t border-gray-200">
						<Button
							type="ghost"
							Icon={PlusIcon}
							onClick={() => customAction?.onClick()}
						>
							{customAction?.label}{' '}
						</Button>
					</div>
				</>
			) : null}
		</components.MenuList>
	);
};

// This is used to find a nested object by ID and return the parent objects
function findPathById(items, targetId, path = []) {
	for (const item of items) {
		const newPath = [...path, item];
		if (item.id === targetId) {
			return newPath;
		}
		if (item.children) {
			const result = findPathById(item.children, targetId, newPath);
			if (result) return result;
		}
	}
	return null;
}

export default ({ item, refreshAllAssets, refreshItem }) => {
	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	console.log('settings: ', settings);

	const [extended, setExtended] = useState(
		settings?.user?.extendedAssetAttributes,
	);

	const [form, setForm] = useState({});

	const [editing, setEditing] = useState(null);

	const { setOpen: openNewCategory } = useModal('addCategoryModal');
	const { open, setOpen: openLocationModal } = useModal('addLocation');
	const { setOpen: openNewVendor } = useModal('addVendorModal');
	const { open: selectLocationOptions, setOpen: openSelectLocation } =
		useModal('selectLocation');
	const { setOpen: openManageAttributes } = useModal('manageAttributes');

	const { value: widgets, status: isLoadingWidgets } = useAsync(
		getWidgetsByType,
		{
			defaultPayload: { type: 'assetDashboard' },
		},
	);

	const { value: tags, status: isLoadingTags } = useAsync(getTags);

	const {
		value: moduleFields,
		execute: onRefreshModuleFields,
		status: isLoadingModuleFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: ASSETS_MODULE_ID, active: true },
	});

	const {
		execute: onUpdateAsset,
		status: isUpdatingAsset,
		error: hasUpdatingError,
	} = useAsync(updateAsset, {
		forceCallback: true,
		immediate: false,
		showNotification: { success: 'Asset has been updated', error: 'raw' },
	});

	const { value: locations, status: isLoadingLocations } =
		useAsync(getLocations);

	const { value: categories, status: isLoadingCategories } = useAsync(
		getModuleCategories,
		{ defaultPayload: { id: ASSETS_MODULE_ID } },
	);

	const { value: vendors, status: isLoadingVendors } = useAsync(getContacts);

	useEffect(() => {
		if (hasUpdatingError) {
			setForm(convertRawAssetToModulizizedForm(item));
		}
	}, [hasUpdatingError]);

	useEffect(() => {
		if (
			settings &&
			typeof settings?.user?.extendedAssetAttributes !== 'undefined'
		) {
			setExtended(settings?.user?.extendedAssetAttributes);
		}
	}, [settings]);

	const convertRawAssetToModulizizedForm = (asset) => {
		let itemForm = {};
		moduleFields?.combined?.map((field) => {
			const isDefault = field?.isDefault;

			if (isDefault) {
				if (asset[field?.key] || asset[`${field?.key}Id`]) {
					itemForm[field?.key] = {
						fieldId: field?.id,
						value:
							asset[`${field?.key}_id`] || asset[`${field?.key}Id`]
								? asset[field?.key]?.name
								: asset[field?.key],
					};
				}
			} else {
				const val = asset?.custom_attributes?.find(
					(f) => f.attribute_field_id === field.id,
				);
				itemForm[field?.key] = {
					value: val?.backupValue || val?.value,
					fieldId: field.id,
					id: val?.id,
				};
			}
		});
		return itemForm;
	};

	const reducedAttributes = moduleFields?.combined
		?.filter((field) => field?.label !== 'Tags')
		?.reduce(
			(prev, cur) => {
				//if (form[cur?.key]?.value) {
				prev[cur?.isDefault ? 'default' : 'custom'].push(cur);
				//}
				return prev;
			},
			{ default: [], custom: [] },
		);

	const getPath = {
		manufacturer: {
			path: 'manufacturer.name',
		},
		model: {
			path: 'model.name',
		},
		location: {
			path: 'location.id',
		},
		category: {
			path: 'category.id',
		},
	};

	const getValue = (field, obj) => {
		if (field?.isDefault) {
			return objectAccess(getPath[field?.key]?.path || field?.key, obj || item);
		} else {
			return (obj || item)?.custom_attributes?.find(
				(o) => o.attribute_field_id === field?.id,
			)?.originalValue;
		}
	};

	const vendorOptions = vendors?.rows?.map((vendor) => ({
		label: vendor?.companyName || getUserFullName(vendor),
		value: vendor?.id,
	}));

	const categoryOptions = categories?.map((category) => ({
		label: category?.name,
		value: category?.id,
	}));

	const booleanOptions = [
		{ label: 'True', value: 'true' },
		{ label: 'False', value: 'false' },
	];

	const locationOptionsTree = createDataTree(locations?.rows, 'children');

	console.log('widgetSettings: ', settings?.user);
	console.log(
		'widgets: ',
		widgets,
		getAvailableWidgets(
			'assetDashboard',
			widgets?.map((w) => w.key),
		),
	);

	return (
		<div className="sm:mt-4 mb-10 pb-20">
			<div>
				<ul role="list" className="divide-y divide-gray-100 text-xs">
					{reducedAttributes?.default?.map((field) => {
						const value = getValue(field) || '-';
						const isEditable = field?.isDefault
							? !['manufacturer', 'model'].includes(field?.key)
							: true;

						return (
							<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 flex-1">
									<div className="text-gray-500 pt-2">{field?.label}</div>
									<div className="">
										{isEditable ? (
											field?.type === 'text' ? (
												<input
													value={
														editing?.key === field?.key
															? editing?.newValue
															: value
													}
													className={classNames(
														'outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
													)}
													onChange={(e) => {
														setEditing({
															key: field?.key,
															newValue: e?.target?.value,
														});
													}}
													onFocus={() => {
														setEditing({ key: field?.key });
													}}
													onBlur={() => {
														if (
															editing.key === field?.key &&
															typeof editing?.newValue !== 'undefined' &&
															editing?.newValue !== getValue(field, item)
														) {
															onUpdateAsset(
																{
																	id: item?.id,
																	[editing?.key]: editing?.newValue,
																},
																(payload, err) => {
																	if (err) {
																		setEditing({
																			...editing,
																			newValue: getValue(field, item),
																		});
																	} else {
																		refreshItem({ id: item?.id }, null, {
																			silent: true,
																		});
																	}
																},
															);
														}
													}}
												/>
											) : field?.type === 'category' ? (
												<ReactSelect
													unstyled
													backspaceRemovesValue={false}
													styles={{
														control: (provided) => {
															return {
																...provided,
																outline: '',
																cursor: 'pointer',
															};
														},
														option: (provided, state) => {
															return {
																...provided,
																paddingLeft: `${
																	(state.data.depth || 0) * 16 + 10
																}px`,
															};
														},
													}}
													classNames={{
														control: ({ isFocused }) =>
															classNames(
																isFocused &&
																	'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																'px-0 sm:px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
															),
														input: ({ isFocused }) => '',
														valueContainer: () => '',
														placeholder: () => 'py-2.5',
														menu: () =>
															'shadow-md border border-gray-100 bg-white',
														option: ({ isFocused, isSelected, isGroup }) =>
															classNames(
																isFocused &&
																	'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																isSelected &&
																	'hover:border-indigo-500 !border-indigo-500 ',
																'hover:cursor-pointer px-0 sm:px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
															),
														noOptionsMessage: () => 'py-4',
													}}
													placeholder="-"
													noOptionsMessage={() => 'No category found'}
													components={{
														ClearIndicator,

														...(value
															? { DropdownIndicator: () => null }
															: { DropdownIndicator }),
														MenuList: (props) => (
															<CustomMenuList
																{...props}
																customAction={{
																	label: 'Add a new category',
																	onClick: () =>
																		openNewCategory({
																			moduleId: ASSETS_MODULE_ID,
																			onSubmit: (newCategory) =>
																				onUpdateAsset(
																					{
																						id: item?.id,
																						category:
																							newCategory?.value || null,
																					},
																					(payload) => {
																						refreshItem(
																							{ id: item?.id },
																							null,
																							{
																								silent: true,
																							},
																						);
																						refreshAllAssets(null, null, {
																							silent: true,
																						});
																					},
																				),
																		}),
																}}
															/>
														),
													}}
													value={categoryOptions?.find(
														(o) => o.value === value,
													)}
													isDisabled={isLoadingCategories === 'pending'}
													options={categoryOptions}
													onChange={(newValue) => {
														onUpdateAsset(
															{
																id: item?.id,
																category: newValue?.value || null,
															},
															(payload) => {
																refreshItem({ id: item?.id }, null, {
																	silent: true,
																});
															},
														);
													}}
													isClearable
												/>
											) : field?.type === 'location' ? (
												<div className="relative w-full">
													<div
														//contentEditable
														className={classNames(
															'w-full pr-10 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
														)}
														onClick={() =>
															openSelectLocation({
																locations,
																onSelect: (selected) => {
																	onUpdateAsset(
																		{
																			id: item?.id,
																			location: selected?.id,
																		},
																		(payload, err) => {
																			refreshItem({ id: item?.id }, null, {
																				silent: true,
																			});
																			refreshAllAssets(null, null, {
																				silent: true,
																			});
																		},
																	);
																},
																selected: value,
															})
														}
													>
														{findPathById(locationOptionsTree, value)
															?.map((o) => o.name)
															?.reverse()
															.map((name, index) => (
																<span
																	className={
																		index === 0
																			? 'text-black'
																			: 'text-gray-400 ml-3'
																	}
																>
																	{name}
																</span>
															)) || '-'}
													</div>
													{value && value !== '-' && (
														<XMarkIcon
															className="absolute right-3 top-0 mt-0 w-8 h-8 text-gray-500 px-2 py-1 cursor-pointer"
															onClick={() => {
																onUpdateAsset(
																	{
																		id: item?.id,
																		location: null,
																	},
																	(payload, err) => {
																		refreshItem({ id: item?.id }, null, {
																			silent: true,
																		});
																		refreshAllAssets(null, null, {
																			silent: true,
																		});
																	},
																);
															}}
														/>
													)}
												</div>
											) : null
										) : (
											<div className="py-2.5 px-0 sm:px-3">{value}</div>
										)}
									</div>
								</div>
							</li>
						);
					})}

					{reducedAttributes?.custom?.length && extended
						? reducedAttributes?.custom
								?.sort((a, b) =>
									a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
								)
								?.map((field) => {
									const value = getValue(field);
									const isEditable = field?.isDefault
										? !['manufacturer', 'model'].includes(field?.key)
										: true;

									return (
										<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 flex-1">
												<div className="text-gray-500 pt-2">{field?.label}</div>
												<div className="">
													{isEditable
														? (() => {
																switch (field?.type) {
																	case 'text':
																		return (
																			<input
																				placeholder="-"
																				defaultValue={value}
																				className={classNames(
																					'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
																				)}
																				onBlur={(e) => {
																					onUpdateAsset(
																						{
																							id: item?.id,
																							customAttributes: [
																								{
																									fieldId: field?.id,
																									value: e.target.value,
																								},
																							],
																						},
																						(payload, err) => {
																							refreshItem(
																								{ id: item?.id },
																								null,
																								{
																									silent: true,
																								},
																							);
																						},
																					);
																				}}
																			/>
																		);
																	case 'textarea':
																		return (
																			<textarea
																				className={classNames(
																					'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
																				)}
																				placeholder="-"
																				height="auto"
																				rows={3}
																				defaultValue={value}
																				onBlur={(e) => {
																					onUpdateAsset(
																						{
																							id: item?.id,
																							customAttributes: [
																								{
																									fieldId: field?.id,
																									value: e.target.value,
																								},
																							],
																						},
																						(payload, err) => {
																							refreshItem(
																								{ id: item?.id },
																								null,
																								{
																									silent: true,
																								},
																							);
																						},
																					);
																				}}
																			/>
																		);
																	case 'numeric':
																		return (
																			<input
																				placeholder="-"
																				defaultValue={value}
																				className={classNames(
																					'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
																				)}
																				onBlur={(e) => {
																					onUpdateAsset(
																						{
																							id: item?.id,
																							customAttributes: [
																								{
																									fieldId: field?.id,
																									value: e.target.value,
																								},
																							],
																						},
																						(payload, err) => {
																							refreshItem(
																								{ id: item?.id },
																								null,
																								{
																									silent: true,
																								},
																							);
																						},
																					);
																				}}
																				type="number"
																				pattern="^-?[0-9]\d*\.?\d*$"
																			/>
																		);
																	case 'location':
																		return (
																			<div className="relative w-full">
																				<div
																					contentEditable
																					className={classNames(
																						'w-full pr-10 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-0 sm:px-3 h-full w-full',
																					)}
																					onFocus={() => {
																						openSelectLocation({
																							locations,
																							onSelect: (selected) => {
																								onUpdateAsset(
																									{
																										id: item?.id,
																										customAttributes: [
																											{
																												fieldId: field?.id,
																												value: selected?.id,
																											},
																										],
																									},
																									(payload, err) => {
																										refreshItem(
																											{ id: item?.id },
																											null,
																											{
																												silent: true,
																											},
																										);
																										refreshAllAssets(
																											null,
																											null,
																											{
																												silent: true,
																											},
																										);
																									},
																								);
																							},
																							selected: value,
																						});
																					}}
																				>
																					{findPathById(
																						locationOptionsTree,
																						value,
																					)
																						?.map((o) => o.name)
																						?.reverse()
																						.map((name, index) => (
																							<span
																								className={
																									index === 0
																										? 'text-black'
																										: 'text-gray-400 ml-3'
																								}
																							>
																								{name}
																							</span>
																						)) || '-'}
																				</div>
																				{value && value !== '-' && (
																					<XMarkIcon
																						className="absolute right-3 top-0 mt-0 w-8 h-8 text-gray-500 px-2 py-1 cursor-pointer"
																						onClick={() => {
																							onUpdateAsset(
																								{
																									id: item?.id,
																									customAttributes: [
																										{
																											fieldId: field?.id,
																											value: null,
																										},
																									],
																								},
																								(payload, err) => {
																									refreshItem(
																										{ id: item?.id },
																										null,
																										{
																											silent: true,
																										},
																									);
																									refreshAllAssets(null, null, {
																										silent: true,
																									});
																								},
																							);
																						}}
																					/>
																				)}
																			</div>
																		);
																	case 'category':
																		return (
																			<ReactSelect
																				unstyled
																				backspaceRemovesValue={false}
																				styles={{
																					control: (provided) => {
																						return {
																							...provided,
																							outline: '',
																							cursor: 'pointer',
																						};
																					},
																					option: (provided, state) => {
																						return {
																							...provided,
																							paddingLeft: `${
																								(state.data.depth || 0) * 16 +
																								10
																							}px`,
																						};
																					},
																				}}
																				classNames={{
																					control: ({ isFocused }) =>
																						classNames(
																							isFocused &&
																								'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																							'px-0 sm:px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																						),
																					input: ({ isFocused }) => '',
																					valueContainer: () => '',
																					placeholder: () => 'py-2.5',
																					menu: () =>
																						'shadow-md border border-gray-100 bg-white',
																					option: ({
																						isFocused,
																						isSelected,
																						isGroup,
																					}) =>
																						classNames(
																							isFocused &&
																								'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																							isSelected &&
																								'hover:border-indigo-500 !border-indigo-500 ',
																							'hover:cursor-pointer px-0 sm:px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																						),
																					noOptionsMessage: () => 'py-4',
																				}}
																				placeholder="-"
																				noOptionsMessage={() =>
																					'No category found'
																				}
																				components={{
																					ClearIndicator,

																					...(value
																						? { DropdownIndicator: () => null }
																						: { DropdownIndicator }),
																					MenuList: (props) => (
																						<CustomMenuList
																							{...props}
																							customAction={{
																								label: 'Add a new category',
																								onClick: () =>
																									openNewCategory({
																										moduleId: ASSETS_MODULE_ID,
																										onSubmit: (newCategory) =>
																											onUpdateAsset(
																												{
																													id: item?.id,
																													customAttributes: [
																														{
																															fieldId:
																																field?.id,
																															value:
																																newCategory?.value,
																														},
																													],
																												},
																												(payload) => {
																													refreshItem(
																														{ id: item?.id },
																														null,
																														{
																															silent: true,
																														},
																													);
																												},
																											),
																									}),
																							}}
																						/>
																					),
																				}}
																				value={categoryOptions?.find(
																					(o) => o.value === value,
																				)}
																				isDisabled={
																					isLoadingCategories === 'pending'
																				}
																				options={categoryOptions}
																				onChange={(newValue) => {
																					onUpdateAsset(
																						{
																							id: item?.id,
																							customAttributes: [
																								{
																									fieldId: field?.id,
																									value:
																										newValue?.value || null,
																								},
																							],
																						},
																						(payload) => {
																							refreshItem(
																								{ id: item?.id },
																								null,
																								{
																									silent: true,
																								},
																							);
																							refreshAllAssets(null, null, {
																								silent: true,
																							});
																						},
																					);
																				}}
																				isClearable
																			/>
																		);
																	case 'boolean':
																		return (
																			<ReactSelect
																				unstyled
																				backspaceRemovesValue={false}
																				styles={{
																					control: (provided) => {
																						return {
																							...provided,
																							outline: '',
																							cursor: 'pointer',
																						};
																					},
																					option: (provided, state) => {
																						return {
																							...provided,
																							paddingLeft: `${
																								(state.data.depth || 0) * 16 +
																								10
																							}px`,
																						};
																					},
																				}}
																				classNames={{
																					control: ({ isFocused }) =>
																						classNames(
																							isFocused &&
																								'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																							'px-0 sm:px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																						),
																					input: ({ isFocused }) => '',
																					valueContainer: () => '',
																					placeholder: () => 'py-2.5',
																					menu: () =>
																						'shadow-md border border-gray-100 bg-white',
																					option: ({
																						isFocused,
																						isSelected,
																						isGroup,
																					}) =>
																						classNames(
																							isFocused &&
																								'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																							isSelected &&
																								'hover:border-indigo-500 !border-indigo-500 ',
																							'hover:cursor-pointer px-0 sm:px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																						),
																					noOptionsMessage: () => 'py-4',
																				}}
																				placeholder="-"
																				isSearchable={false}
																				components={{
																					ClearIndicator,

																					...(value
																						? { DropdownIndicator: () => null }
																						: { DropdownIndicator }),
																				}}
																				value={booleanOptions?.find(
																					(o) => o.value === value,
																				)}
																				options={booleanOptions}
																				onChange={(newValue) => {
																					onUpdateAsset(
																						{
																							id: item?.id,
																							customAttributes: [
																								{
																									fieldId: field?.id,
																									value:
																										newValue?.value || null,
																								},
																							],
																						},
																						(payload) => {
																							refreshItem(
																								{ id: item?.id },
																								null,
																								{
																									silent: true,
																								},
																							);
																						},
																					);
																				}}
																				isClearable
																			/>
																		);
																	case 'vendor':
																		return (
																			<ReactSelect
																				unstyled
																				backspaceRemovesValue={false}
																				styles={{
																					control: (provided) => {
																						return {
																							...provided,
																							outline: '',
																							cursor: 'pointer',
																						};
																					},
																					option: (provided, state) => {
																						return {
																							...provided,
																							paddingLeft: `${
																								(state.data.depth || 0) * 16 +
																								10
																							}px`,
																						};
																					},
																				}}
																				classNames={{
																					control: ({ isFocused }) =>
																						classNames(
																							isFocused &&
																								'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																							'px-0 sm:px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																						),
																					input: ({ isFocused }) => '',
																					valueContainer: () => '',
																					placeholder: () => 'py-2.5',
																					menu: () =>
																						'shadow-md border border-gray-100 bg-white',
																					option: ({
																						isFocused,
																						isSelected,
																						isGroup,
																					}) =>
																						classNames(
																							isFocused &&
																								'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																							isSelected &&
																								'hover:border-indigo-500 !border-indigo-500 ',
																							'hover:cursor-pointer px-0 sm:px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																						),
																					noOptionsMessage: () => 'py-4',
																				}}
																				placeholder="-"
																				noOptionsMessage={() => 'Not found'}
																				components={{
																					ClearIndicator,

																					...(value
																						? { DropdownIndicator: () => null }
																						: { DropdownIndicator }),
																					MenuList: (props) => (
																						<CustomMenuList
																							{...props}
																							customAction={{
																								label: 'Add a company',
																								onClick: () =>
																									openNewVendor({
																										onSubmit: (vendor) => {
																											onUpdateAsset(
																												{
																													id: item?.id,
																													customAttributes: [
																														{
																															fieldId:
																																field?.id,
																															value:
																																vendor?.value,
																														},
																													],
																												},
																												(payload) => {
																													refreshItem(
																														{ id: item?.id },
																														null,
																														{
																															silent: true,
																														},
																													);
																												},
																											);
																										},
																									}),
																							}}
																						/>
																					),
																				}}
																				value={vendorOptions?.find(
																					(o) => o.value === value,
																				)}
																				isDisabled={
																					isLoadingVendors === 'pending'
																				}
																				options={vendorOptions}
																				onChange={(newValue) => {
																					onUpdateAsset(
																						{
																							id: item?.id,
																							customAttributes: [
																								{
																									fieldId: field?.id,
																									value:
																										newValue?.value || null,
																								},
																							],
																						},
																						(payload) => {
																							refreshItem(
																								{ id: item?.id },
																								null,
																								{
																									silent: true,
																								},
																							);
																							refreshAllAssets(null, null, {
																								silent: true,
																							});
																						},
																					);
																				}}
																				isClearable
																			/>
																		);
																}
															})()
														: null}
												</div>
											</div>
										</li>
									);
								})
						: null}

					<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
						<div className="grid grid-cols-2 gap-x-6 flex-1">
							<div className="text-gray-500 pt-2">Tags</div>
							<div className="">
								<div className="text-sm text-gray-400 mt-1.5 font-light flex items-center flex-wrap wrap">
									{item?.tags?.map((tag) => (
										<Badge
											isBeta
											size="xxs"
											className={classNames(
												TAG_COLOR_OPTIONS.find(
													(color) => color?.key === tag?.color,
												)?.value,
												'mr-1.5 mt-1.5 cursor-pointer',
											)}
											onClick={() => navigate(`/assets/tags/${tag?.id}`)}
										>
											{tag?.name}
											<XMarkIcon
												className="w-6 h-6 pl-2 text-gray-600"
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();

													onUpdateAsset(
														{
															id: item?.id,
															tagToRemove: tag?.id,
														},
														() => {
															refreshItem({ id: item?.id }, null, {
																silent: true,
															});
														},
													);
												}}
											/>
										</Badge>
									))}

									<Menu as="div" className="relative inline-block text-left">
										<div>
											<Menu.Button className="">
												<Badge
													isBeta
													size="xxs"
													className={classNames(
														'bg-gray-100 text-gray-800 ring-gray-300 mr-1.5 mt-1.5 cursor-pointer px-2 py-1.5',
													)}
													onClick={() => {}}
												>
													<PlusIcon className="w-3 h-3 text-gray-600" />
												</Badge>
											</Menu.Button>
										</div>

										<Menu.Items
											transition
											className="absolute left-0 z-50 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
										>
											<div className="px-3 py-2 flex items-center">
												<div className="w-full">
													<TemplateReactSelect
														placeholder="Select a tag to add"
														options={tags?.map((tag) => ({
															label: tag?.name,
															value: tag?.id,
															color: tag?.color,
														}))}
														skip={item?.tags?.map((tag) => tag.id)}
														blurInputOnSelect
														onChange={(index, selected) => {
															onUpdateAsset(
																{
																	id: item?.id,
																	tagToAdd: selected?.value,
																},
																() => {
																	refreshItem({ id: item?.id }, null, {
																		silent: true,
																	});
																},
															);
														}}
													/>
												</div>

												<PlusCircleIcon
													className="w-10 h-10 py-2 text-gray-600 ml-1 cursor-pointer"
													onClick={() =>
														openTagModal({
															onSubmit: (selected) => {
																onUpdateAsset(
																	{
																		id: item?.id,
																		tagToAdd: selected?.value,
																	},
																	() => {
																		refreshItem({ id: item?.id }, null, {
																			silent: true,
																		});
																	},
																);
															},
														})
													}
												/>
											</div>
										</Menu.Items>
									</Menu>
								</div>
							</div>
						</div>
					</li>
				</ul>
				<div className="hidden sm:block mb-6 mt-3 sm:mb-0">
					{reducedAttributes?.custom?.length ? (
						<>
							<a
								className="text-indigo-800 hover:text-indigo-600 text-xs cursor-pointer mr-1.5"
								onClick={() => {
									updateSettings(
										{
											type: 'user',
											key: 'extendedAssetAttributes',
											value: !extended,
										},
										{ showNotification: false },
									);
									setExtended(!extended);
								}}
							>
								{extended
									? 'Hide all fields'
									: `Show (${reducedAttributes?.custom?.length}) fields`}
							</a>
							<span className="text-gray-300 mr-1.5">-</span>
						</>
					) : null}
					<a
						className="text-indigo-800 hover:text-indigo-600 text-xs cursor-pointer"
						onClick={() => {
							openManageAttributes({
								moduleId: ASSETS_MODULE_ID,
								onSelect: (id, active) => {
									console.log('select:', id, active);
								},
							});
						}}
					>
						Manage fields
					</a>
				</div>
			</div>

			<div className="hidden sm:block">
				<WidgetDashboard
					widgetSettings={settings?.user?.widgetSettings}
					isLoading={isLoadingWidgets === 'pending' || isLoadingSettings}
					availableWidgets={getAvailableWidgets(
						'assetDashboard',
						widgets?.map((w) => w.key),
					)}
					activeWidgets={
						settings?.user?.widgets?.assetDashboard //|| ['asset-overview']
					}
					onChange={(state) => {
						if (
							settings?.user?.widgets?.assetDashboard?.length !==
								state?.length ||
							!isEqual(settings?.user?.widgets?.assetDashboard, state)
						) {
							updateSettings({
								type: 'user',
								key: 'widgets',
								value: {
									...settings?.user?.widgets,
									assetDashboard: state,
								},
							});
						}
					}}
				/>
			</div>
		</div>
	);
	return (
		<div className="my-10 grid grid-cols-4 gap-6 pb-20">
			<StatusChartWidget item={item} />
			<OverviewWidget item={item} />
			<LarianTechAssetWidget assetId={item?.id} />
			<TimelineWidget item={item} />
			<ContractWidget item={item} />
		</div>
	);
};
